import { Tooltip } from "antd";
import { RefObject, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";
import { ISubaccount, useAccountFamily, useSubaccounts } from "../../../hooks/api/admin/useSubaccounts";
import { getProfitTextColor, shortenAddress } from "../../../utils/strings";
import {
  ChipAddress,
  ChipCount,
  ChipName,
  ChipRole,
  FamilyBarWrapper,
  FamilyChip,
  FamilyChips,
  FamilySeparator,
  FamilyTotals,
  FamilyViewButton,
  MutedText,
  RelationPill,
  RoleBadge,
  TooltipFooter,
  TooltipHeader,
  TooltipList,
  TooltipRow,
  TooltipStat,
} from "./style";
import { accountTotals, formatUsd, pluralize, sortSubaccounts, subaccountTotals } from "./utils";

// Dashboard rows only look themselves up once they have stayed on screen for a moment,
// so scrolling past thousands of accounts does not fire a request per row.
const IN_VIEW_DELAY_MS = 250;

type InViewCallback = (isIntersecting: boolean) => void;
const inViewCallbacks = new Map<Element, InViewCallback>();
let inViewObserver: IntersectionObserver | undefined;

const getInViewObserver = () => {
  if (!inViewObserver) {
    inViewObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => inViewCallbacks.get(entry.target)?.(entry.isIntersecting)),
      { rootMargin: "200px 0px" }
    );
  }
  return inViewObserver;
};

const useHasBeenInView = (ref: RefObject<Element>, enabled: boolean) => {
  const [hasBeenInView, setHasBeenInView] = useState(!enabled);

  useEffect(() => {
    const element = ref.current;
    if (!enabled || hasBeenInView || !element) return undefined;

    let timeout: ReturnType<typeof setTimeout> | undefined;
    const observer = getInViewObserver();
    inViewCallbacks.set(element, (isIntersecting) => {
      clearTimeout(timeout);
      if (isIntersecting) timeout = setTimeout(() => setHasBeenInView(true), IN_VIEW_DELAY_MS);
    });
    observer.observe(element);

    return () => {
      clearTimeout(timeout);
      observer.unobserve(element);
      inViewCallbacks.delete(element);
    };
  }, [enabled, hasBeenInView, ref]);

  return hasBeenInView;
};

export const useAccountLabel = () => {
  const { records } = useContext(DataContext);
  return (address: string) => records[address] || shortenAddress(address);
};

export const subaccountLabel = (sub: ISubaccount) => sub.name || "Unnamed";

export const isSameAddress = (a?: string, b?: string) => Boolean(a && b && a.toLowerCase() === b.toLowerCase());

// Must match the account page tab (TabsEnum.FAMILY)
export const FAMILY_TAB = "family";

function SubaccountTooltip({ master, subaccounts }: { master: string; subaccounts: ISubaccount[] }) {
  return (
    <TooltipList>
      <TooltipHeader>{pluralize(subaccounts.length, "subaccount")}</TooltipHeader>
      {sortSubaccounts(subaccounts).map((sub) => {
        const totals = accountTotals(sub.balances, sub.positions);
        return (
          <TooltipRow key={sub.subaccount} to={`/account/${sub.subaccount}`} onClick={(e) => e.stopPropagation()}>
            <span>
              {subaccountLabel(sub)}
              {sub.archived ? <MutedText> (archived)</MutedText> : null}
            </span>
            <TooltipStat>{formatUsd(totals.collateral)}</TooltipStat>
            <TooltipStat>{pluralize(totals.positions, "pos")}</TooltipStat>
            <span>{shortenAddress(sub.subaccount)}</span>
          </TooltipRow>
        );
      })}
      <TooltipFooter to={`/account/${master}/${FAMILY_TAB}`} onClick={(e) => e.stopPropagation()}>
        Open family view ↗
      </TooltipFooter>
    </TooltipList>
  );
}

interface ISubaccountTagProps {
  account: string;
  // Wait until the tag scrolls into view before fetching (used by the dashboard table)
  lazy?: boolean;
}

/**
 * Indicator next to an account address: "SUB of <master>" for a subaccount (click to open its master),
 * "MASTER · N subs" for a master account (hover to list and open its subaccounts). Renders nothing otherwise.
 */
export function SubaccountTag({ account, lazy }: ISubaccountTagProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const navigate = useNavigate();
  const getLabel = useAccountLabel();
  const shouldFetch = useHasBeenInView(ref, Boolean(lazy));
  const { parent, subaccounts, isSubaccount, isMaster } = useSubaccounts(shouldFetch ? account : undefined, {
    poll: !lazy,
  });

  if (isSubaccount && parent) {
    return (
      <Tooltip title={`Subaccount of ${getLabel(parent)} (${parent}). Click to open the master account.`}>
        <RelationPill
          ref={ref}
          variant="sub"
          roleLabel="SUB"
          role="link"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/account/${parent}`);
          }}
        >
          of {getLabel(parent)}
        </RelationPill>
      </Tooltip>
    );
  }

  if (isMaster) {
    return (
      <Tooltip
        title={<SubaccountTooltip master={account} subaccounts={subaccounts} />}
        mouseLeaveDelay={0.2}
        overlayStyle={{ maxWidth: 480 }}
      >
        <RelationPill ref={ref} variant="master" roleLabel="MASTER" onClick={(e) => e.stopPropagation()}>
          {pluralize(subaccounts.length, "sub")}
        </RelationPill>
      </Tooltip>
    );
  }

  // Empty placeholder so the in-view observer has something to watch
  return <span ref={ref} />;
}

function ChipTooltip({ address, sub }: { address: string; sub?: ISubaccount }) {
  if (!sub) return <span>{address}</span>;
  const totals = accountTotals(sub.balances, sub.positions);
  return (
    <TooltipList>
      <span>{address}</span>
      <span>
        Collateral {formatUsd(totals.collateral)} · uPnL{" "}
        <span style={{ color: getProfitTextColor(totals.upnl) }}>{formatUsd(totals.upnl)}</span> ·{" "}
        {pluralize(totals.positions, "position")}
      </span>
      {sub.archived ? <MutedText>Archived</MutedText> : null}
    </TooltipList>
  );
}

interface IAccountFamilyBarProps {
  account?: string;
  // Current account page tab, kept when switching to another account in the family
  tab: string;
  onOpenFamily: () => void;
}

/**
 * Strip above the account page tabs that shows which family the open account belongs to:
 * the master first, then every subaccount, with the open account highlighted. Each chip switches
 * to that account on the same tab, and the right side totals the subaccounts.
 */
export function AccountFamilyBar({ account, tab, onOpenFamily }: IAccountFamilyBarProps) {
  const getLabel = useAccountLabel();
  const { master, subaccounts, isSubaccount, hasFamily } = useAccountFamily(account);

  if (!hasFamily || !master) return null;

  const totals = subaccountTotals(subaccounts);
  const isMasterOpen = !isSubaccount;
  const masterLabel = getLabel(master);

  return (
    <FamilyBarWrapper>
      <RoleBadge variant={isMasterOpen ? "master" : "sub"}>{isMasterOpen ? "Master account" : "Subaccount"}</RoleBadge>
      <FamilyChips>
        <Tooltip title={<ChipTooltip address={master} />} placement="bottom">
          <FamilyChip to={`/account/${master}/${tab}`} variant="master" isCurrent={isMasterOpen}>
            <ChipRole>Master</ChipRole>
            <ChipName>{masterLabel}</ChipName>
            {masterLabel !== shortenAddress(master) ? <ChipAddress>{shortenAddress(master)}</ChipAddress> : null}
          </FamilyChip>
        </Tooltip>
        <FamilySeparator>›</FamilySeparator>
        {sortSubaccounts(subaccounts).map((sub) => (
          <Tooltip key={sub.subaccount} title={<ChipTooltip address={sub.subaccount} sub={sub} />} placement="bottom">
            <FamilyChip
              to={`/account/${sub.subaccount}/${tab}`}
              variant="sub"
              isCurrent={isSameAddress(sub.subaccount, account)}
              isArchived={sub.archived}
            >
              {
                // Names are what ops recognise; the address (also in the tooltip) identifies unnamed ones
                sub.name ? <ChipName>{sub.name}</ChipName> : <ChipAddress>{shortenAddress(sub.subaccount)}</ChipAddress>
              }
              {sub.archived ? <MutedText>archived</MutedText> : null}
              {sub.positions.length ? <ChipCount>{sub.positions.length}</ChipCount> : null}
            </FamilyChip>
          </Tooltip>
        ))}
      </FamilyChips>
      <FamilyTotals>
        <span>
          {pluralize(subaccounts.length, "sub")}: collateral <strong>{formatUsd(totals.collateral)}</strong>
        </span>
        <span>
          uPnL <strong style={{ color: getProfitTextColor(totals.upnl) }}>{formatUsd(totals.upnl)}</strong>
        </span>
        <span>
          positions <strong>{totals.positions}</strong>
        </span>
      </FamilyTotals>
      {tab !== FAMILY_TAB ? <FamilyViewButton onClick={onOpenFamily}>Family view</FamilyViewButton> : null}
    </FamilyBarWrapper>
  );
}
